import {
  queryOptions,
  useMutation,
  UseMutationOptions,
  useQueryClient
} from '@tanstack/react-query';
import { useInvalidateCurrentProducts, useInvalidateProposalInformation } from 'bff';
import {
  InvestmentDetailsChangeProductAndSourceOfFundsRequest,
  InvestmentDetailsDeleteAllUnitTransfersRequest,
  InvestmentDetailsDeleteCashTransferRequest,
  InvestmentDetailsDeleteFundAllocationSplitRequest,
  InvestmentDetailsDeleteLumpSumPremiumRequest,
  InvestmentDetailsDeletePhasingInRequest,
  InvestmentDetailsDeletePhasingInTargetFundRequest,
  InvestmentDetailsDeleteRecurringContributionPremiumRequest,
  InvestmentDetailsDeleteRegularWithdrawalRequest,
  InvestmentDetailsDeleteRequest,
  InvestmentDetailsDeleteTFSATTransferPremiumRequest,
  InvestmentDetailsDeleteUnitTransferRequest,
  InvestmentDetailsFetchRequest,
  InvestmentDetailsSubmitFundSelectionRequest,
  InvestmentDetailsSubmitInvestmentDetailsRequest,
  LookupsGetOneFromCacheRequest,
  ResponseError,
  Result,
  ResultOfInvestmentDetailsProductViewModel,
  ResultStates,
  InvestmentDetailsDeleteOnceOffInvestmentRequest,
  InvestmentDetailsDeleteJustPortfolioIncomeAssetSplitRequest,
  InvestmentDetailsDeleteJustPortfolioRequest
} from 'bff/client';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';
import { minutesToMilliseconds } from 'date-fns';
import { clientService } from 'stores/client-store/client-store';
import { useInvalidateJustPortfolioDetails } from '../applications/just-portfolio/JustPortfolioApi';
import { useInvalidatePaymentDetails } from '../applications/payment-details/PaymentDetailsApi';
import { useInvalidateProposalSummaryDetails } from './ProposalSummaryApi';
import { logger } from '@sanlam-glacier/app-common/modules/logger';
import { useInvalidateExposureDetails } from './ExposureApi';
import { useInvalidateCapturedSigningDetails } from '../signing/SigningApi';

const investmentKeys = {
  base: () => ['investment-details'] as const,
  fetch: (request: InvestmentDetailsFetchRequest) => [...investmentKeys.base(), request],
  submit: () => [...investmentKeys.base(), 'submit'] as const,
  fundSelection: (request: LookupsGetOneFromCacheRequest) =>
    [...investmentKeys.base(), 'fund-selection', request] as const
};

export const submitInvestmentDetailsKey = investmentKeys.submit();

export const useInvalidateInvestmentDetails = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: investmentKeys.base(),
      refetchType: 'all'
    });
};

export const getInvestmentDetailsOptions = (request: InvestmentDetailsFetchRequest) => {
  const { investmentDetailsApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: investmentKeys.fetch(request),
    queryFn: () => investmentDetailsApi.investmentDetailsFetch(request),
    select: (response) => response.data!,
    staleTime: minutesToMilliseconds(5)
  });
};

export const useSaveInvestmentProductDetailsInvestmentDetails = (
  options?: UseDisplayErrorMutationOptions<Result, InvestmentDetailsSubmitInvestmentDetailsRequest>
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateInvestmentDetails = useInvalidateInvestmentDetails();
  const invalidatePaymentDetails = useInvalidatePaymentDetails();
  const invalidateProposalSummaryDetails = useInvalidateProposalSummaryDetails();
  const invalidateExposureDetails = useInvalidateExposureDetails();
  const invalidateSigningDetails = useInvalidateCapturedSigningDetails();

  return useMutation({
    mutationKey: investmentKeys.submit(),
    mutationFn: (request: InvestmentDetailsSubmitInvestmentDetailsRequest) =>
      investmentDetailsApi.investmentDetailsSubmitInvestmentDetails(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);

        await Promise.allSettled([invalidateInvestmentDetails()]);
      } else {
        await options?.onSuccess?.(data, variables, ctx);

        await Promise.allSettled([
          invalidateInvestmentDetails(),
          invalidateProposalInformation(),
          invalidatePaymentDetails(),
          invalidateProposalSummaryDetails(),
          invalidateExposureDetails(),
          invalidateSigningDetails()
        ]);
      }
    }
  });
};

export const useSaveInvestmentProductDetailsFundSelection = (
  options?: UseDisplayErrorMutationOptions<Result, InvestmentDetailsSubmitFundSelectionRequest>
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateInvestmentDetails = useInvalidateInvestmentDetails();
  const invalidatePaymentDetails = useInvalidatePaymentDetails();
  const invalidateProposalSummaryDetails = useInvalidateProposalSummaryDetails();
  const invalidateExposureDetails = useInvalidateExposureDetails();
  const invalidateJustPortfolioDetails = useInvalidateJustPortfolioDetails();

  return useMutation({
    mutationKey: investmentKeys.submit(),
    mutationFn: (request: InvestmentDetailsSubmitFundSelectionRequest) =>
      investmentDetailsApi.investmentDetailsSubmitFundSelection(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateInvestmentDetails(),
          invalidatePaymentDetails(),
          invalidateProposalSummaryDetails(),
          invalidateExposureDetails(),
          invalidateJustPortfolioDetails()
        ]);

        await options?.onSuccess?.(data, variables, ctx);
      }
    }
  });
};

/**
 * Deletes a Product by its ID.
 * @param InvestmentDetailsDeleteRequest An object containing the productId
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeleteInvestmentProduct = (
  options?: UseMutationOptions<Result, ResponseError, InvestmentDetailsDeleteRequest>
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateCurrentProducts = useInvalidateCurrentProducts();
  const invalidateStep3 = useInvalidateInvestmentDetails();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details', 'useDeleteProposal'],
    mutationFn: async (deleteProductRequest: InvestmentDetailsDeleteRequest) =>
      await investmentDetailsApi.investmentDetailsDelete(deleteProductRequest),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateCurrentProducts(),
          invalidateStep3(),
          options?.onSuccess?.(...args)
        ]);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

/**
 * Deletes a Target fund by its ID.
 * @param InvestmentDetailsDeletePhasingInTargetFundRequest An object containing the phasingInTargetFundId
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeletePhasingInTargetFund = (
  options?: UseMutationOptions<
    Result,
    ResponseError,
    InvestmentDetailsDeletePhasingInTargetFundRequest
  >
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateStep3 = useInvalidateInvestmentDetails();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details', 'useDeleteTargetFund'],
    mutationFn: async (
      deleteTargetFundRequest: InvestmentDetailsDeletePhasingInTargetFundRequest
    ) =>
      await investmentDetailsApi.investmentDetailsDeletePhasingInTargetFund(
        deleteTargetFundRequest
      ),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateStep3(),
          options?.onSuccess?.(...args)
        ]);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

/**
 * Deletes the unit transfer fund.
 * @param InvestmentDetailsDeleteUnitTransferRequest An object containing the unit transfer fund identifier.
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeleteUnitTransfer = (
  options?: UseMutationOptions<Result, ResponseError, InvestmentDetailsDeleteUnitTransferRequest>
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateStep3 = useInvalidateInvestmentDetails();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details', 'useDeleteUnitTransfer'],
    mutationFn: async (deleteProductRequest: InvestmentDetailsDeleteUnitTransferRequest) =>
      await investmentDetailsApi.investmentDetailsDeleteUnitTransfer(deleteProductRequest),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([invalidateProposalInformation(), invalidateStep3()]);
        options?.onSuccess?.(...args);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

/**
 * Deletes the fund allocation split.
 * @param InvestmentDetailsDeleteFundAllocationSplitRequest An object containing the product selected asset identifier.
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeleteFundAllocationSplit = (
  options?: UseMutationOptions<
    Result,
    ResponseError,
    InvestmentDetailsDeleteFundAllocationSplitRequest
  >
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateStep3 = useInvalidateInvestmentDetails();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details', 'useDeleteFundAllocationSplit'],
    mutationFn: async (deleteProductRequest: InvestmentDetailsDeleteFundAllocationSplitRequest) =>
      await investmentDetailsApi.investmentDetailsDeleteFundAllocationSplit(deleteProductRequest),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateStep3(),
          options?.onSuccess?.(...args)
        ]);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

export const getFundsForSelection = (request: LookupsGetOneFromCacheRequest) => {
  const { lookupsApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: investmentKeys.fundSelection(request),
    queryFn: () => lookupsApi.lookupsGetOneFromCache(request),
    staleTime: Number.POSITIVE_INFINITY
  });
};

/**
 * Deletes a Lump Sum by its ID.
 * @param InvestmentDetailsDeleteLumpSumPremiumRequest An object containing the productId
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeleteInvestmentLumpSum = (
  options?: UseMutationOptions<Result, ResponseError, InvestmentDetailsDeleteLumpSumPremiumRequest>
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateStep3 = useInvalidateInvestmentDetails();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details-lump-sum', 'useDeleteInvestmentLumpSum'],
    mutationFn: async (deleteLumpSumRequest: InvestmentDetailsDeleteLumpSumPremiumRequest) =>
      await investmentDetailsApi.investmentDetailsDeleteLumpSumPremium(deleteLumpSumRequest),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateStep3(),
          options?.onSuccess?.(...args)
        ]);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

export const useDeleteInvestmentInternalTransfer = (
  options?: UseMutationOptions<
    Result,
    ResponseError,
    InvestmentDetailsDeleteTFSATTransferPremiumRequest
  >
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateStep3 = useInvalidateInvestmentDetails();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details-internal-transfer', 'useDeleteInvestmentInternalTransfer'],
    mutationFn: async (
      deleteInternalTransferRequest: InvestmentDetailsDeleteTFSATTransferPremiumRequest
    ) =>
      await investmentDetailsApi.investmentDetailsDeleteTFSATTransferPremium(
        deleteInternalTransferRequest
      ),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateStep3(),
          options?.onSuccess?.(...args)
        ]);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

/**
 * Deletes a Cash Transfer by its ID.
 * @param InvestmentDetailsDeleteCashTransferRequest An object containing the productId
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeleteInvestmentCashTransfer = (
  options?: UseMutationOptions<Result, ResponseError, InvestmentDetailsDeleteCashTransferRequest>
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateStep3 = useInvalidateInvestmentDetails();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details-cash-transfer', 'useDeleteInvestmentCashTransfer'],
    mutationFn: async (deleteCashTransferRequest: InvestmentDetailsDeleteCashTransferRequest) =>
      await investmentDetailsApi.investmentDetailsDeleteCashTransfer(deleteCashTransferRequest),
    onSuccess: async (...args) => {
      try {
        await invalidateProposalInformation();
        await invalidateStep3();
        if (options?.onSuccess) {
          options.onSuccess(...args);
        }
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

/**
 * Deletes a phase in by its ID.
 * @param InvestmentDetailsDeletePhasingInRequest An object containing the productId
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeleteInvestmentPhasingIn = (
  options?: UseMutationOptions<Result, ResponseError, InvestmentDetailsDeletePhasingInRequest>
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateStep3 = useInvalidateInvestmentDetails();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details-phasing-in', 'useDeleteInvestmentPhasingIn'],
    mutationFn: async (deletePhasingInRequest: InvestmentDetailsDeletePhasingInRequest) =>
      await investmentDetailsApi.investmentDetailsDeletePhasingIn(deletePhasingInRequest),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateStep3(),
          options?.onSuccess?.(...args)
        ]);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

/**
 * Deletes unit transfer sources by productId.
 * @param InvestmentDetailsDeleteAllUnitTransfersRequest An object containing the productId
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeleteAllUnitTransfers = (
  options?: UseMutationOptions<
    Result,
    ResponseError,
    InvestmentDetailsDeleteAllUnitTransfersRequest
  >
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateStep3 = useInvalidateInvestmentDetails();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details-all-unit-transfers', 'useDeleteAllUnitTransfers'],
    mutationFn: async (
      deleteAllUnitTransfersRequest: InvestmentDetailsDeleteAllUnitTransfersRequest
    ) =>
      await investmentDetailsApi.investmentDetailsDeleteAllUnitTransfers(
        deleteAllUnitTransfersRequest
      ),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([invalidateProposalInformation(), invalidateStep3()]);

        options?.onSuccess?.(...args);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

/**
 * Deletes a recurring contribution by its ID.
 * @param InvestmentDetailsDeleteRecurringContributionPremiumRequest An object containing the productId
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeleteInvestmentRecurringContribution = (
  options?: UseMutationOptions<
    Result,
    ResponseError,
    InvestmentDetailsDeleteRecurringContributionPremiumRequest
  >
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateStep3 = useInvalidateInvestmentDetails();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: [
      'investment-details-recurring-contribution',
      'useDeleteInvestmentRecurringContribution'
    ],
    mutationFn: async (
      deleteRecurringContributionRequest: InvestmentDetailsDeleteRecurringContributionPremiumRequest
    ) =>
      await investmentDetailsApi.investmentDetailsDeleteRecurringContributionPremium(
        deleteRecurringContributionRequest
      ),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateStep3(),
          options?.onSuccess?.(...args)
        ]);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

/**
 * Deletes a regular withdrawal by its ID.
 * @param InvestmentDetailsDeleteRegularWithdrawalRequest An object containing the productId
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeleteInvestmentRegularWithdrawal = (
  options?: UseMutationOptions<
    Result,
    ResponseError,
    InvestmentDetailsDeleteRegularWithdrawalRequest
  >
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateStep3 = useInvalidateInvestmentDetails();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: [
      'investment-details-recurring-contribution',
      'useDeleteInvestmentRegularWithdrawal'
    ],
    mutationFn: async (
      deleteRegularWithdrawalRequest: InvestmentDetailsDeleteRegularWithdrawalRequest
    ) =>
      await investmentDetailsApi.investmentDetailsDeleteRegularWithdrawal(
        deleteRegularWithdrawalRequest
      ),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateStep3(),
          options?.onSuccess?.(...args)
        ]);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

/**
 * This hook is used to change the source of money for a living annuity.
 * Because of the legal implications of the change to the source, this will delete the current product,
 * and create a new product with the newly selected source of money
 * @param options Options to override mutation defaults
 * @returns The response of the useMutation hook
 */
export const useProductChangeSourceOfMoney = (
  options?: UseMutationOptions<
    ResultOfInvestmentDetailsProductViewModel,
    ResponseError,
    InvestmentDetailsChangeProductAndSourceOfFundsRequest
  >
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateStep3 = useInvalidateInvestmentDetails();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: [
      'investment-details-recurring-contribution',
      'useDeleteInvestmentRecurringContribution'
    ],
    mutationFn: async (
      changeSourceRequest: InvestmentDetailsChangeProductAndSourceOfFundsRequest
    ) =>
      await investmentDetailsApi.investmentDetailsChangeProductAndSourceOfFunds(
        changeSourceRequest
      ),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateStep3(),
          options?.onSuccess?.(...args)
        ]);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Please use the logger. You know who you are. Disabling for now.
        console.error('Error during cache invalidation:', error);
      }
    }
  };

  return useMutation(mutationOptions);
};

/**
 * Deletes a once-off investment (including lump sum, cash transfer, and unit transfer) by proposal ID
 * @param InvestmentDetailsDeleteOnceOffInvestmentRequest An object containing the proposal ID
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeleteOnceOffInvestment = (
  options?: UseMutationOptions<
    Result,
    ResponseError,
    InvestmentDetailsDeleteOnceOffInvestmentRequest
  >
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateCurrentProducts = useInvalidateCurrentProducts();
  const invalidateStep3 = useInvalidateInvestmentDetails();
  const invalidateProposalSummaryDetails = useInvalidateProposalSummaryDetails();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details', 'useDeleteOnceOffInvestment'],
    mutationFn: async (
      deleteOnceOffInvestmentRequest: InvestmentDetailsDeleteOnceOffInvestmentRequest
    ) =>
      await investmentDetailsApi.investmentDetailsDeleteOnceOffInvestment(
        deleteOnceOffInvestmentRequest
      ),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateStep3(),
          invalidateProposalInformation(),
          invalidateCurrentProducts(),
          invalidateProposalSummaryDetails()
        ]);
        await options?.onSuccess?.(...args);
      } catch (error) {
        if (error instanceof Error)
          logger.error({ message: 'Error during cache invalidation:', object: error as Error });
      }
    }
  };

  return useMutation(mutationOptions);
};

export const useDeleteJustFund = (
  options?: UseMutationOptions<
    Result,
    ResponseError,
    InvestmentDetailsDeleteJustPortfolioIncomeAssetSplitRequest
  >
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateCurrentProducts = useInvalidateCurrentProducts();
  const invalidateStep3 = useInvalidateInvestmentDetails();
  const invalidateProposalSummaryDetails = useInvalidateProposalSummaryDetails();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details', 'useDeleteJustFund'],
    mutationFn: async (
      deleteJustFundRequest: InvestmentDetailsDeleteJustPortfolioIncomeAssetSplitRequest
    ) =>
      await investmentDetailsApi.investmentDetailsDeleteJustPortfolioIncomeAssetSplit(
        deleteJustFundRequest
      ),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateStep3(),
          invalidateProposalInformation(),
          invalidateCurrentProducts(),
          invalidateProposalSummaryDetails()
        ]);
        await options?.onSuccess?.(...args);
      } catch (error) {
        if (error instanceof Error)
          logger.error({ message: 'Error during cache invalidation:', object: error as Error });
      }
    }
  };

  return useMutation(mutationOptions);
};

/**
 * Deletes a just portfolio (including step 5 just details) by just portfolio ID
 * @param InvestmentDetailsDeleteJustPortfolioRequest An object containing the just portfolio ID
 * @param options UseMutationOptions from React-Query. Note that if you pass in an onSuccess handler it will
 * be invoked after an invalidation of get proposal queries.
 * @returns The useMutate query hook result.
 */
export const useDeleteJustFundPortfolio = (
  options?: UseMutationOptions<Result, ResponseError, InvestmentDetailsDeleteJustPortfolioRequest>
) => {
  const { investmentDetailsApi } = clientService.getApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateCurrentProducts = useInvalidateCurrentProducts();
  const invalidateStep3 = useInvalidateInvestmentDetails();
  const invalidateProposalSummaryDetails = useInvalidateProposalSummaryDetails();
  const invalidateJustPortfolioDetails = useInvalidateJustPortfolioDetails();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details', 'useDeleteJustFundPortfolio'],
    mutationFn: async (deleteJustFundRequest: InvestmentDetailsDeleteJustPortfolioRequest) =>
      await investmentDetailsApi.investmentDetailsDeleteJustPortfolio(deleteJustFundRequest),
    onSuccess: async (...args) => {
      try {
        await Promise.allSettled([
          invalidateStep3(),
          invalidateProposalInformation(),
          invalidateCurrentProducts(),
          invalidateProposalSummaryDetails(),
          invalidateJustPortfolioDetails()
        ]);
        await options?.onSuccess?.(...args);
      } catch (error) {
        if (error instanceof Error)
          logger.error({ message: 'Error during cache invalidation:', object: error as Error });
      }
    }
  };

  return useMutation(mutationOptions);
};
